import { useSyncExternalStore, useEffect } from 'react';

import { fetchProjectLevelSystems as getSystems } from '../api';
import { TKSystem } from '../types';

type SystemState = {
  projectSystems: string[];
  globalSystems: TKSystem[];
  systemsLoaded: boolean;
};

/**
 * variable system definitions
 */
let initialSystemState: SystemState = {
  /** project level custom systems */
  projectSystems: [],
  /** product level built-in systems */
  globalSystems: [],
  /** loading flag */
  systemsLoaded: false,
};

/**
 * callback functions would dynamically change
 */
let listeners: (() => void)[] = [];

const emitChange = () => {
  for (const listener of listeners) {
    listener();
  }
};

export const systemStore = {
  /** cache global systems */
  setGlobalSystems(systems: TKSystem[]) {
    initialSystemState = {
      ...initialSystemState,
      globalSystems: systems,
      systemsLoaded: true,
    };
    emitChange();
  },
  /** cache project or custom systems */
  setProjectSystems(systems: string[]) {
    initialSystemState = {
      ...initialSystemState,
      projectSystems: systems,
      systemsLoaded: true,
    };
    emitChange();
  },
  /**
   * client side add new system in drawing symbol creation
   */
  addCustemSystem(system: string) {
    const { projectSystems } = initialSystemState;
    // ! dont create repetition ! - 2025/04/10
    if (projectSystems.includes(system)) return;
    initialSystemState = {
      ...initialSystemState,
      projectSystems: projectSystems.concat(system),
    };
    emitChange();
  },
  // == inline method ==
  subscribe(listener: () => void) {
    listeners = [...listeners, listener];
    return () => {
      // remove the newly added listener to achieve unsubscribe
      listeners = listeners.filter((l) => l !== listener);
    };
  },
  /**
   * exposed state change handler for `useSyncExternalStore`
   * @returns an immutable catalogues store state
   */
  getSnapshot() {
    return initialSystemState;
  },
};

/**
 * Get project and global systems
 * @date 2025/04/09
 * @param projectId optional project id
 * @returns project(custom) systems and global systems
 */
export const useSystemStore = (projectId?: string) => {
  const { subscribe, getSnapshot } = systemStore;
  const systemState = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (initialSystemState.systemsLoaded) return;
    if (!projectId) return; // no loading at drawing page

    getSystems(projectId)
      .then((resp) => {
        systemStore.setProjectSystems(resp.data as string[]);
      })
      .catch((err) => {
        console.error(err);
      });
    // lock reload
    initialSystemState.systemsLoaded = true;
  }, [projectId]);

  return {
    ...systemState,
  };
};
